// Native Workspace app context menus. The shell owns the single
// ContextDropdown instance; this module only supplies focused actions for
// native app records and a common opener for visible "more" controls,
// right-click, and touch long-press.

const _sameId = (left, right) => String(left || '') === String(right || '')

const _reminderFor = (root, id) => (root?.reminders || []).find((item) => _sameId(item?.id, id))
const _broadcastFor = (root, id) =>
  (root?.announcements || []).find((item) => _sameId(item?.id, id))

const _ownsReminder = (root, reminder) => {
  const me = String(root?.currentUserEmail || '').toLowerCase()
  return !!me && me === String(reminder?.ownerEmail || '').toLowerCase()
}

const _shellClose = (root) => root.update({ contextDropdown: null }, { preventFetch: true })

const _menuNotify = (el, payload) => {
  try {
    el.call('openNotification', payload)
  } catch (_) {
    /* notifications are advisory only */
  }
}

export const openAppContextMenu = function openAppContextMenu(event, appContext, options = {}) {
  const root = this?.state?.root
  if (!root || !appContext?.kind) return

  event?.preventDefault?.()
  event?.stopPropagation?.()

  const rect = this.node?.getBoundingClientRect?.()
  const anchor = !!options.anchor
  const x = anchor && rect ? rect.right : Number(event?.clientX) || rect?.left || 0
  const y = anchor && rect ? rect.bottom : Number(event?.clientY) || rect?.bottom || 0
  const active = root.contextDropdown?.appContextMenu

  if (
    root.contextDropdown?.open &&
    active?.kind === appContext.kind &&
    _sameId(active?.id, appContext.id)
  ) {
    _shellClose(root)
    return
  }

  root.update(
    {
      contextDropdown: {
        open: true,
        x,
        y,
        itemsFn: 'appContextMenuItems',
        actionFn: 'appContextMenuAction',
        appContextMenu: { kind: appContext.kind, id: appContext.id || null }
      }
    },
    { preventFetch: true }
  )
}

export const appContextMenuItems = function appContextMenuItems(root) {
  const context = root?.contextDropdown?.appContextMenu
  if (!context?.kind) return []

  if (context.kind === 'reminder') {
    const reminder = _reminderFor(root, context.id)
    if (!reminder) return []
    const items = [
      {
        key: 'reminder-toggle',
        action: 'reminder-toggle',
        label: reminder.done
          ? 'workspace.ctxMenu.reminder.markOpen'
          : 'workspace.ctxMenu.reminder.markDone',
        icon: reminder.done ? 'reload' : 'checkmark'
      },
      { key: 'reminder-divider-actions', divider: true }
    ]
    // "Convert to ticket…" deletes the reminder once the ticket exists, so it
    // is the owner's, like Delete (functions/reminders.js
    // confirmConvertReminderToTask).
    if (_ownsReminder(root, reminder)) {
      items.push({
        key: 'reminder-ticket',
        action: 'reminder-ticket',
        label: 'workspace.ctxMenu.reminder.toTicket',
        icon: 'checkOval'
      })
    }
    items.push({
      key: 'reminder-event',
      action: 'reminder-event',
      label: 'workspace.ctxMenu.reminder.toEvent',
      icon: 'dateOutline'
    })
    if (_ownsReminder(root, reminder)) {
      items.push(
        { key: 'reminder-divider-danger', divider: true },
        {
          key: 'reminder-delete',
          action: 'reminder-delete',
          label: 'workspace.ctxMenu.reminder.delete',
          icon: 'trash',
          danger: true
        }
      )
    }
    return items
  }

  if (context.kind === 'broadcast') {
    const post = _broadcastFor(root, context.id)
    if (!post) return []
    let canEdit = false
    try {
      canEdit = !!this.call('canEditBroadcast', post, root)
    } catch (_) {
      canEdit = false
    }
    const items = [
      { key: 'broadcast-open', action: 'broadcast-open', label: 'workspace.ctxMenu.broadcast.open' }
    ]
    if (canEdit) {
      items.push(
        {
          key: 'broadcast-edit',
          action: 'broadcast-edit',
          label: 'workspace.ctxMenu.broadcast.edit',
          icon: 'edit'
        },
        { key: 'broadcast-divider-danger', divider: true },
        {
          key: 'broadcast-delete',
          action: 'broadcast-delete',
          label: 'workspace.ctxMenu.broadcast.delete',
          icon: 'trash',
          danger: true
        }
      )
    }
    return items
  }

  if (context.kind === 'system-health') {
    return [
      {
        key: 'system-refresh',
        action: 'system-refresh',
        label: 'workspace.ctxMenu.system.refresh',
        icon: 'reload'
      },
      { key: 'system-divider-copy', divider: true },
      {
        key: 'system-copy-summary',
        action: 'system-copy-summary',
        label: 'workspace.ctxMenu.system.copySummary',
        icon: 'copy'
      }
    ]
  }

  return []
}

// The status words of the summary — the same keys the /system page's own
// STATUS table names (pages/system.js), written out so each stays greppable.
// A status this table does not know is shown as its raw id.
const _SUMMARY_STATUS_KEY = {
  healthy: 'workspace.system.status.healthy',
  degraded: 'workspace.system.status.degraded',
  building: 'workspace.system.status.building',
  failed: 'workspace.system.status.failed',
  down: 'workspace.system.status.failed',
  pending: 'workspace.system.status.pending',
  probe_error: 'workspace.system.status.probeError',
  none: 'workspace.system.status.noData'
}

// The copied summary is also the success toast's message, so every word of
// it resolves through the bundle.
const _systemSummary = (root, el) => {
  const counts = new Map()
  for (const cell of Object.values(root?.systemMatrix || {})) {
    const status = String(cell?.status || 'none')
    counts.set(status, (counts.get(status) || 0) + 1)
  }
  const details = [...counts.entries()]
    .filter(([, count]) => count > 0)
    // A count and a status word — "3 healthy" / "3 ჯანსაღი": the pair reads
    // in this order in both shipped languages, so it needs no sentence key.
    .map(
      ([status, count]) => `${count} ${el.call('polyglot', _SUMMARY_STATUS_KEY[status] || status)}`
    )
    .join(', ')
  return el
    .call('polyglot', 'workspace.ctxMenu.system.summary')
    .replace('{details}', details || el.call('polyglot', 'workspace.ctxMenu.system.summaryEmpty'))
}

const _copySystemSummary = async (el, root) => {
  const summary = _systemSummary(root, el)
  try {
    if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) {
      throw new Error('Clipboard is unavailable')
    }
    await navigator.clipboard.writeText(summary)
    _menuNotify(el, {
      type: 'success',
      title: el.call('polyglot', 'workspace.ctxMenu.system.copied'),
      message: summary
    })
  } catch (_) {
    _menuNotify(el, {
      type: 'error',
      title: el.call('polyglot', 'workspace.ctxMenu.system.copyFailed'),
      message: el.call('polyglot', 'workspace.ctxMenu.system.copyFailedBody')
    })
  }
}

export const appContextMenuAction = async function appContextMenuAction(action) {
  const root = this?.state?.root
  const context = root?.contextDropdown?.appContextMenu
  if (!root || !context) return
  _shellClose(root)

  if (context.kind === 'reminder') {
    const reminder = _reminderFor(root, context.id)
    if (!reminder) return
    if (action === 'reminder-toggle') {
      this.call('toggleReminder', reminder.id)
      return
    }
    // The row's icons open the same two asks (functions/reminders.js), so a
    // reminder has ONE delete confirm and ONE convert confirm.
    if (action === 'reminder-ticket') {
      this.call('confirmConvertReminderToTask', reminder.id)
      return
    }
    if (action === 'reminder-event') {
      this.call('convertReminderToEvent', reminder.id)
      return
    }
    if (action === 'reminder-delete' && _ownsReminder(root, reminder)) {
      this.call('confirmDeleteReminder', reminder.id)
    }
    return
  }

  if (context.kind === 'broadcast') {
    const post = _broadcastFor(root, context.id)
    if (!post) return
    if (action === 'broadcast-open') {
      this.call('openBroadcastDetail', post)
      return
    }
    let canEdit = false
    try {
      canEdit = !!this.call('canEditBroadcast', post, root)
    } catch (_) {
      canEdit = false
    }
    if (!canEdit) return
    if (action === 'broadcast-edit') {
      this.call('openBroadcastComposer', post)
      return
    }
    if (action === 'broadcast-delete') this.call('confirmDeleteBroadcast', post)
    return
  }

  if (context.kind === 'system-health') {
    if (action === 'system-refresh') {
      root._systemHealthOps?.refresh?.()
      return
    }
    if (action === 'system-copy-summary') await _copySystemSummary(this, root)
  }
}
